import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { AppButton } from "@libs/zapi-ui/custom";
import $api from "@/zapi-admin/shared/api/axios";
import { AppLoader } from "@/zapi-admin/shared/components/AppLoader";
import { JsonViewer } from "@/zapi-admin/shared/components/JsonViewer/ui/JsonViewer";
import { MarkdownView } from "@/zapi-admin/shared/components/MarkdownView/MarkdownView";
import { fetchClaudeRunFile } from "../model/api";
import type { TClaudeRun } from "../model/types";
import { formatBytes } from "../lib/format";

const fileUrl = (runId: string, name: string) =>
  `${$api.defaults.baseURL}/admin/claude-runs/${runId}/file?name=${encodeURIComponent(name)}`;

function FileContent({ runId, name }: { runId: string; name: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["claude-run-file", runId, name],
    queryFn: () => fetchClaudeRunFile(runId, name)
  });
  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <AppLoader />
      </div>
    );
  }
  if (error || data === undefined) return <div className="text-[13px] text-red-700">The file could not be loaded.</div>;
  if (name.endsWith(".json")) {
    return (
      <div className="h-[70vh]">
        <JsonViewer json={data} />
      </div>
    );
  }
  if (name.endsWith(".md")) return <MarkdownView content={data} />;
  return <pre className="whitespace-pre-wrap wrap-break-word text-[12px] leading-[18px] font-mono">{data}</pre>;
}

export function RunFiles({ run }: { run: TClaudeRun }) {
  const [selected, setSelected] = useState<string | null>(null);

  if (run.logStatus !== "stored" || run.logFiles.length === 0) {
    return <div className="text-[13px] text-zinc-500">No log files are stored for this run.</div>;
  }

  return (
    <div className="grid grid-cols-[320px_1fr] gap-6" data-testid="run-files">
      <ul className="divide-y divide-zinc-100 border border-zinc-150 rounded-[10px] self-start">
        {run.logFiles.map((file) => (
          <li
            key={file.name}
            className={`flex items-center justify-between gap-2 px-3 py-2 text-[13px] cursor-pointer ${
              selected === file.name ? "bg-zinc-100" : "hover:bg-zinc-50"
            }`}
            onClick={() => setSelected(file.name)}
            data-testid={`run-file-${file.name}`}
          >
            <span className="font-mono truncate">{file.name}</span>
            <span className="flex items-center gap-2 shrink-0 text-zinc-500">
              {formatBytes(file.bytes)}
              <a
                href={fileUrl(run.id, file.name)}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="hover:text-zinc-900"
                title="Open the raw file"
              >
                <ExternalLink size={14} />
              </a>
            </span>
          </li>
        ))}
      </ul>
      <div className="min-w-0">
        {selected ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[13px] text-zinc-700">{selected}</span>
              <AppButton appearance="text" color="zinc950" size="sm" onClick={() => setSelected(null)}>
                Close
              </AppButton>
            </div>
            <FileContent runId={run.id} name={selected} />
          </>
        ) : (
          <div className="text-[13px] text-zinc-500">Pick a file to view it here.</div>
        )}
      </div>
    </div>
  );
}
